import type { ReactNode } from 'react';

// ------------------------------------------------------------------
// Surface — story 2-147ta (P10 §1.3 "surface hierarchy", §2.7 row 5, DOD).
// The base visual treatment `Card` (this file's own sibling, `Card.tsx`)
// and every other "this needs separation from canvas" component builds on,
// closing P10 §2.7 row 5's own named gap verbatim: "Flat surface / card
// wrapper — Named but not yet built ... no standalone Surface/Card file
// exists in components/ui today ... Built by 2-147ta."
//
// P10 §1.3 already named FOUR surfaces (canvas → surface → elevated overlay
// → glass/gradient) but only wrote them down as a rule for a component
// AUTHOR to apply by hand (`VisualLanguageRoleSurfacesGallery`,
// `DesignSystemPage.tsx`, story 2-136 — deliberately untouched by this
// story, see that gallery function's own header comment: "no component file
// is touched by this story"). This component is the first REAL primitive
// that rule resolves through, instead of every consumer re-deriving the
// same `rounded-hairline border border-border bg-card` (or
// `.glass-card`/`bg-popover` + `shadow-elevated`) className by hand — the
// exact 44-file pattern this story's own duplication measurement found
// (grep for `rounded-(hairline|xl|lg|2xl)…border…bg-card` across `src`,
// recorded in SESSION-HANDOFF.md).
//
// Four variants, one per §1.3 role. `bare` renders no visual treatment at
// all (padding/`as` only) — the same "no card/slot contract" escape hatch
// `Layout.tsx`'s `Section` already documents needing when a caller wants
// rhythm without a card wrapper.
// ------------------------------------------------------------------

export type SurfaceVariant = 'bare' | 'flat' | 'elevated' | 'glass';
export type SurfacePadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';

// `elevated` reuses `Tooltip.tsx`'s own real elevated-overlay recipe
// (`border-border-strong` + `bg-popover` + `shadow-elevated`) verbatim —
// the SAME three classes, not a second derivation of the role P10 §1.3
// already fixed ("any surface in the elevated-overlay role ... carries
// `--shadow-elevated` and nothing else").
const VARIANT_CLASS: Record<SurfaceVariant, string> = {
  bare: '',
  flat: 'rounded-hairline border border-border bg-card text-card-foreground',
  elevated: 'rounded-hairline border border-border-strong bg-popover text-popover-foreground shadow-elevated',
  glass: 'glass-card',
};

const PADDING_CLASS: Record<SurfacePadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  // `lg` (p-5) is the established "collection card" convention this story
  // formalizes rather than invents — `TemplateCard.tsx`'s own header
  // comment names it explicitly: "matches the p-5 flat-card convention this
  // lane already uses for other collection items (BillingPage's metric
  // cards, OrgAccessPage's role cards, OrgFeaturesPage's category
  // sections)." `Card`'s own default (see `Card.tsx`).
  lg: 'p-5',
  // `xl` (p-6) is `GallerySection.tsx`'s own page-level section padding.
  xl: 'p-6',
};

// Local, not exported — same "one file, one local Tag alias" convention
// `Layout.tsx`/`Headers.tsx` already use rather than sharing one across
// files (avoids a cross-file type-only import for a two-line alias).
type Tag = 'div' | 'article' | 'section' | 'li';

export interface SurfaceProps {
  /** Which of P10 §1.3's four surface roles to render. Defaults to `'flat'` — the role §1.3 itself names as "the DEFAULT the 'this looks like a card' instinct should reach for." */
  variant?: SurfaceVariant;
  /** Defaults to `'none'` — a bare wrapper. `Card` (this file's sibling) sets `'lg'` for its own default. */
  padding?: SurfacePadding;
  as?: Tag;
  /** Escape hatch for a genuinely component-local addition (e.g. `overflow-hidden`, a dashed border override) — never a second border/radius/background declaration, which would defeat this component's own point. */
  className?: string;
  'data-testid'?: string;
  children?: ReactNode;
  /**
   * Narrow escape hatch for a real `data-*` state hook a specific composed
   * consumer needs (e.g. `TaskCard.tsx`'s own `data-accent`, used by CSS/e2e
   * to read which accent state is active) — deliberately scoped to `data-*`
   * only, not a full untyped prop passthrough, so this stays a closed
   * primitive surface rather than growing per-consumer.
   */
  [dataAttr: `data-${string}`]: string | undefined;
}

export function Surface({
  variant = 'flat',
  padding = 'none',
  as = 'div',
  className = '',
  'data-testid': testId,
  children,
  ...dataAttrs
}: SurfaceProps): JSX.Element {
  const Component = as;
  return (
    <Component
      className={`${VARIANT_CLASS[variant]} ${PADDING_CLASS[padding]} ${className}`.trim()}
      data-testid={testId}
      {...dataAttrs}
    >
      {children}
    </Component>
  );
}
